import { CustomChipProps } from '@ds';
import { EmployeeLabels, ResourceLogisticsStatus } from '@/api/apis/case';
import { AffiliationType, EmployeeRole, OcsConsoleStatus, ReservationType } from '@/api/apis/resources';

// TODO: Should be part of Case Api
// TODO: ResourceLogisticsStatus enum differs from schema api -> affects texts
export const resourceLogisticsStatusNames: Record<ResourceLogisticsStatus, string> = {
  [ResourceLogisticsStatus.None]: 'None',
  [ResourceLogisticsStatus.BookingSent]: 'Assigned',
  [ResourceLogisticsStatus.Booked]: 'Assigned',
  [ResourceLogisticsStatus.OnCase]: 'Assigned',
  [ResourceLogisticsStatus.OCSDeployed]: 'OCS Deployed',
  [ResourceLogisticsStatus.OCSDone]: 'Done',
  [ResourceLogisticsStatus.OCSOnCase]: 'OCS On Case',
  [ResourceLogisticsStatus.Available]: 'Available',
  [ResourceLogisticsStatus.Reserved]: 'Reserved',
  [ResourceLogisticsStatus.DeployedToDonorHospital]: 'Deployed to DH',
  [ResourceLogisticsStatus.AtDonorHospital]: 'At DH',
  [ResourceLogisticsStatus.DeployedFromDonorHospital]: 'In Transit to TX',
  [ResourceLogisticsStatus.DeployedToTX]: 'Deployed to TXC',
  [ResourceLogisticsStatus.AtTXCenter]: 'At TXC',
  [ResourceLogisticsStatus.DeployedFromTXCenter]: 'Deployed from TXC',
  [ResourceLogisticsStatus.DeployedToHub]: 'Deployed to Hub',
  [ResourceLogisticsStatus.ReturningToBase]: 'Returning to Base',
  [ResourceLogisticsStatus.Done]: 'Done',
  [ResourceLogisticsStatus.OnRest]: 'On Rest',
  [ResourceLogisticsStatus.RecoveryIsCompleted]: 'Recovery is completed',
  [ResourceLogisticsStatus.BookedInMao]: 'Booked in MAO',
  [ResourceLogisticsStatus.CancelledInMao]: 'Cancelled',
};

export const employeeLabelName: Record<EmployeeLabels, string> = {
  [EmployeeLabels.Primary]: 'Primary',
  [EmployeeLabels.Relief]: 'Relief',
  [EmployeeLabels.Shadow]: 'Shadow',
  [EmployeeLabels.Proctor]: 'Proctor',
};

export const employeeLabelLogEventName: Record<EmployeeLabels, string> = {
  [EmployeeLabels.Primary]: 'Primary',
  [EmployeeLabels.Relief]: 'Relief',
  [EmployeeLabels.Shadow]: 'Shadowing',
  [EmployeeLabels.Proctor]: 'Proctoring',
};

export const employeeLabelColors: Record<EmployeeLabels, CustomChipProps['color']> = {
  [EmployeeLabels.Primary]: 'success',
  [EmployeeLabels.Relief]: 'relief',
  [EmployeeLabels.Shadow]: 'shadow',
  [EmployeeLabels.Proctor]: 'proctor',
};

export const employeeLabelWeight: Record<EmployeeLabels, number> = {
  [EmployeeLabels.Primary]: 1,
  [EmployeeLabels.Relief]: 2,
  [EmployeeLabels.Proctor]: 3,
  [EmployeeLabels.Shadow]: 4,
};

// since CS and RS have conflicts in status naming, we have separate mapping
export const csStatusName: Partial<Record<ResourceLogisticsStatus, string>> = {
  [ResourceLogisticsStatus.Reserved]: 'Reserved',
  [ResourceLogisticsStatus.BookingSent]: 'Acknowledgement pending',
  [ResourceLogisticsStatus.Booked]: 'Acknowledged',
  [ResourceLogisticsStatus.DeployedToDonorHospital]: 'Deployed to DH',
  [ResourceLogisticsStatus.AtDonorHospital]: 'At DH',
  [ResourceLogisticsStatus.DeployedFromDonorHospital]: 'In Transit to TX',
  [ResourceLogisticsStatus.AtTXCenter]: 'At TXC',
  [ResourceLogisticsStatus.DeployedFromTXCenter]: 'Deployed from TXC',
  [ResourceLogisticsStatus.Done]: 'Done',
};

export const rsStatusName: Partial<Record<ResourceLogisticsStatus, string>> = {
  [ResourceLogisticsStatus.Reserved]: 'Reserved',
  [ResourceLogisticsStatus.BookingSent]: 'Acknowledgement pending',
  [ResourceLogisticsStatus.Booked]: 'Acknowledged',
  [ResourceLogisticsStatus.DeployedToDonorHospital]: 'Deployed to DH',
  [ResourceLogisticsStatus.AtDonorHospital]: 'At DH',
  [ResourceLogisticsStatus.RecoveryIsCompleted]: 'Recovery is completed',
  [ResourceLogisticsStatus.DeployedFromDonorHospital]: 'Deployed from DH',
  [ResourceLogisticsStatus.Done]: 'Done',
};

export const contractorAffiliationNames: Record<AffiliationType, string> = {
  [AffiliationType.NoContract]: 'No Contract',
  [AffiliationType.OPO]: 'OPO',
  [AffiliationType.TMDXContract]: 'TMDX Contract',
  [AffiliationType.TXCenter]: 'TX Center',
};

export const ocsConsoleStatusName: Record<OcsConsoleStatus, string> = {
  [OcsConsoleStatus.Available]: 'Available',
  [OcsConsoleStatus.Reserved]: 'Reserved',
  [OcsConsoleStatus.OnCase]: 'Assigned',
};

export const RESOURCE_STATUS_TEXT_MAP: Partial<Record<ResourceLogisticsStatus, string>> = {
  [ResourceLogisticsStatus.Available]: 'Available',
  [ResourceLogisticsStatus.Reserved]: 'Reserved',
  [ResourceLogisticsStatus.BookingSent]: 'Acknowledgement pending',
  [ResourceLogisticsStatus.Booked]: 'Acknowledged',
  [ResourceLogisticsStatus.DeployedToDonorHospital]: 'Deployed to DH',
  [ResourceLogisticsStatus.AtDonorHospital]: 'At DH',
  [ResourceLogisticsStatus.DeployedFromDonorHospital]: 'In Transit to TX',
  [ResourceLogisticsStatus.DeployedToTX]: 'Deployed to TXC',
  [ResourceLogisticsStatus.AtTXCenter]: 'At TXC',
  [ResourceLogisticsStatus.DeployedFromTXCenter]: 'Deployed from TXC',
  [ResourceLogisticsStatus.DeployedToHub]: 'Deployed to Hub',
  [ResourceLogisticsStatus.ReturningToBase]: 'Returning to Base',
  [ResourceLogisticsStatus.OCSDone]: 'OCS Done',
  [ResourceLogisticsStatus.OCSDeployed]: 'OCS Deployed',
  [ResourceLogisticsStatus.Done]: 'Done',
  [ResourceLogisticsStatus.OnRest]: 'On Rest',
  [ResourceLogisticsStatus.RecoveryIsCompleted]: 'Recovery is completed',
  [ResourceLogisticsStatus.BookedInMao]: 'Booked in MAO',
  [ResourceLogisticsStatus.CancelledInMao]: 'Cancelled',
};

export const logisticsRoles = [EmployeeRole.ClinicalSpecialist, EmployeeRole.RetrievalSurgeon];

export const reservationTypeNames: Record<ReservationType, string> = {
  [ReservationType.OnCase]: 'On Case',
  [ReservationType.OnRest]: 'On Rest',
  [ReservationType.Reserved]: 'Reserved',
};
